
import React, { useRef, useEffect, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSidebarState } from './SidebarStateContext';
import { ExpandedGroups } from './types';
import SidebarContent from './SidebarContent';
import { useToast } from "@/hooks/use-toast";
import { useAICopilot } from '@/hooks/useAICopilot';
import { cn } from '@/lib/utils';

interface SidebarProps {
  isMobile: boolean;
}

// Memoize the sidebar to prevent re-rendering when only route changes
const Sidebar = memo(({ isMobile }: SidebarProps) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { expandedGroups, toggleGroup, setActiveGroup } = useSidebarState();
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const { toast } = useToast();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isOpen: isAICopilotOpen } = useAICopilot();
  
  // Handle path changes without unmounting the component
  useEffect(() => {
    // Map paths to their corresponding groups
    const pathToGroup: Record<string, keyof ExpandedGroups> = {
      '/investor-directory': 'investors',
      '/investor-crm': 'investors',
      '/pathways': 'investors',
      '/templates': 'tools',
      '/fundraising-stats': 'tools',
      '/pitch-analyzer': 'resources',
      '/valuation-calculator': 'resources',
      '/financial-model-template': 'resources',
      '/mastering-fundraising': 'training',
      '/financial-modeling': 'training',
      '/ma-strategies': 'training',
      '/fundraising-videos': 'training',
      '/manage-subscription': 'account',
      '/settings': 'account',
      '/get-help': 'account',
      '/feedback': 'account',
      '/company': 'company'
    };
    
    const activeGroup = pathToGroup[currentPath];
    if (activeGroup) {
      setActiveGroup(activeGroup);
    }
  }, [currentPath, setActiveGroup]);

  // Close mobile sheet when navigating
  useEffect(() => {
    if (isMobile && sheetOpen) {
      setSheetOpen(false);
    }
  }, [currentPath, isMobile, sheetOpen]);

  const isActive = (path: string) => currentPath === path;

  // Focus trap for accessibility
  useEffect(() => {
    const sidebarEl = sidebarRef.current;
    if (!sidebarEl || !isMobile || !sheetOpen) return;
    
    const focusableElements = sidebarEl.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    
    if (firstElement) firstElement.focus();
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSheetOpen(false);
        return;
      }
      
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };
    
    sidebarEl.addEventListener('keydown', handleKeyDown);
    return () => {
      sidebarEl.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobile, sheetOpen]);

  // Always show the sidebar on desktop, show a sheet on mobile
  if (isMobile) {
    // Mobile sheet rendering code would go here
    return null; // Temporarily returning null for mobile - will be handled by MobileNav
  }

  // On desktop, always render the sidebar
  return (
    <div 
      ref={sidebarRef}
      className="fixed left-[400px] h-full border-r border-border/40 bg-white z-30 shadow-sm w-[220px] pt-0"
      role="navigation"
      aria-label="Main navigation"
    >
      <SidebarContent 
        expandedGroups={expandedGroups}
        toggleGroup={toggleGroup}
        isActive={isActive}
        scrollContainerRef={scrollContainerRef}
        isCompact={false}
      />
    </div>
  );
});

// Add display name for proper debugging
Sidebar.displayName = 'Sidebar';

export default Sidebar;
